import * as React from "react";
import {useEffect, useMemo, useState} from "react";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {resolveTab} from "src/utils/tab.util";
import {Alert, Box, Stack, Tab, Tooltip} from "@mui/material";
import {TabContext} from "@mui/lab";
import ResourceDetailsForm from "./ResourceDetailsForm";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {
    useAllChildResources,
    useAllRootResources
} from "crqlar-react-common/dist/utils/hooks/resources/useAllRootResources";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {ResourceService} from "crqlar-react-common/dist/services/resource/resource.service";
import {Resource} from "crqlar-react-common/src/models/resource/resource.model";
import {useMutation, useQueryClient} from "react-query";
import {enqueueSnackbar} from "notistack";
import Button from "@mui/material/Button";
import {West} from "@mui/icons-material";
import ResourceAvailabilitiesTab from "./ResourceAvailabilitiesTab";
import ResourceServicesTab from "./ResourceServicesTab";
import SettingsEntityHeader from "../layout/SettingsEntityHeader";
import DetailsLoading from "crqlar-react-common/dist/components/DetailsLoading";
import ScrollableTabList from "crqlar-react-common/dist/components/tabs/ScrollableTabList";

const ResourceDetails = () => {
    const {id} = useParams()
    const navigate = useNavigate()
    const {t} = useLocalize()
    const queryClient = useQueryClient()

    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = resolveTab(searchParams.get('tab'), ['basic', 'availabilities', 'services'], 'basic')
    const setActiveTab = (newTab: string) => {
        const nextSearchParams = new URLSearchParams(searchParams)
        nextSearchParams.set('tab', newTab)
        setSearchParams(nextSearchParams)
    }

    const [childrenChangeDetected, setChildrenChangeDetected] = React.useState(false)
    const [childrenChanges, setChildrenChanges] = useState<Map<string, Partial<Resource>>>(new Map())

    const resourceData = useEntity({
        get: async () => {
            if (id && id !== 'new') {
                return ResourceService.getResourceById(id)
            } else {
                return {parentId: searchParams.get('parentId') || undefined, capacity: 1}
            }
        },
        create: ResourceService.createResource,
        update: ResourceService.updateResource,
        delete: (id) => ResourceService.deleteResource({id: [id]}),
        onDeleted: () => {
            navigate('/system/resources', {replace: true})
        },
        onSaved: (resource, wasCreateAction) => {
            if (wasCreateAction) navigate(`/system/resources/${resource.id}`, {replace: true})
            setChildrenChangeDetected(false)
            void queryClient.invalidateQueries('allChildrenResources')
        },
        isValid: (resource) => !!resource.name && (resource.categoryIds?.length ?? 0) > 0,
        additionalDirtyCondition: childrenChangeDetected,
        cleanupBeforeSave: (resource) => {
            persistChildResourcesState(currentChildResources)
            return {
                ...resource,
                name: resource.name?.trim(),
                meta: {
                    ...resource.meta,
                    bookableExternally: resource.meta?.bookableExternally ?? false,
                    makeAllChildrenBookable: resource.meta?.makeAllChildrenBookable ?? false,
                    autoAssignExternally: resource.meta?.autoAssignExternally ?? false,
                    seatsAroundTable: resource.meta?.seatsAroundTable ?? false
                }
            }
        }
    }, id)

    const rootResources = useAllRootResources()
    const resource = resourceData.item
    const disabled = !!resource.managedExternally
    const setResource = resourceData.setItem
    const childResources = useAllChildResources(resource.id ? [resource?.id] : [])

    const parentResource = rootResources.find((r) => r.id === resource.parentId)
    const hasCategory = !!(resource.categoryIds?.length)

    const currentChildResources = useMemo(() =>
            childResources.map(child => {
                const changes = childrenChanges.get(child.id!)
                return changes ? {...child, ...changes} : child
            }),
        [childResources, childrenChanges]
    )

    useEffect(() => {
        setChildrenChangeDetected(childrenChanges?.size > 0)
    }, [currentChildResources])

    const handleRestaurantAutoAssignChange = (autoAssignExternally: boolean) => {
        updateField('meta', {
            ...resource?.meta,
            autoAssignExternally
        })
        const newChildrenChanges = new Map(childrenChanges)
        currentChildResources.forEach(child => {
            const existing = newChildrenChanges.get(child.id!) || {}
            const existingMeta = (existing.meta || child.meta || {}) as Record<string, any>
            newChildrenChanges.set(child.id!, {
                ...existing,
                meta: {...existingMeta, autoAssignExternally}
            })
        })
        setChildrenChanges(newChildrenChanges)
    }

    const handleMakeAllBookable = (bookableExternally: boolean) => {
        updateField('meta', {
            ...resource?.meta,
            makeAllChildrenBookable: bookableExternally,
            ...(bookableExternally && {bookableExternally: true})
        })
        const newChildrenChanges = new Map(childrenChanges)
        currentChildResources.forEach(child => {
            const existing = newChildrenChanges.get(child.id!) || {}
            const existingMeta = (existing.meta || child.meta || {}) as Record<string, any>
            newChildrenChanges.set(child.id!, {...existing, meta: {...existingMeta, bookableExternally}})
        })
        setChildrenChanges(newChildrenChanges)
    }

    const handleShowSeatsAroundTable = (seatsAroundTable: boolean) => {
        updateField('meta', {...resource?.meta, seatsAroundTable})
    }

    const updateMetaField = (field: string, value: any) => {
        updateField('meta', {...resource?.meta, [field]: value})
    }

    const updateChildMetaField = (childResource: Resource, field: string, value: any) => {
        setChildrenChanges(prev => {
            const newMap = new Map(prev)
            const existing = newMap.get(childResource.id!) || {}
            const existingMeta = (existing.meta || childResource.meta || {}) as Record<string, any>
            newMap.set(childResource.id!, {...existing, meta: {...existingMeta, [field]: value}})
            return newMap
        })
    }

    const updateField = (field: keyof Resource, value: any) => {
        setResource({...resource, [field]: value})
    }

    const resourceMutation = useMutation(
        async (resourcesToUpdate: Resource[]) => ResourceService.updateMulti(resourcesToUpdate),
        {
            onSuccess: (updatedResources: Resource[]) => {
                if (updatedResources) enqueueSnackbar(t("Successfully updated children resources"), {variant: 'success'})
            },
            onError: (error: any) => {
                enqueueSnackbar(t(`An error occurred while updating children resources: ${error}`), {variant: 'error'})
            }
        }
    )

    const persistChildResourcesState = (resources: Resource[]) => {
        const updatedResources = resources
            .map(child => {
                const changes = childrenChanges.get(child.id!)
                return changes ? {...child, ...changes} : null
            })
            .filter(Boolean) as Resource[]
        if (updatedResources.length > 0) resourceMutation.mutate(updatedResources)
    }

    const headerSection = (
        <SettingsEntityHeader
            title={resource.name}
            data={resourceData}
            back={() => navigate('/system/resources')}
            restrictEdit={disabled}
            extraActions={resource.parentId && (
                <Button
                    startIcon={<West/>}
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(`/system/resources/${resource.parentId}`)}
                >
                    {t('To')} {parentResource?.name}
                </Button>
            )}
        />
    )

    const titleSection = disabled ? (
        <Stack gap={2}>
            <Alert
                variant={'outlined'}
                severity={'warning'}
                sx={{py: 0, lineHeight: 1, alignItems: 'center'}}
            >
                {t('This entity is managed by an external system and thus cannot be edited here.')}
            </Alert>
        </Stack>
    ) : null

    const tabsSection = (
        <TabContext value={activeTab}>
            <ScrollableTabList onChange={(e, v) => setActiveTab(v)}>
                <Tab label={t('Basic info')} value={'basic'}/>
                {hasCategory ? (
                    <Tab label={t('Working/Opening Hours')} value={'availabilities'} disabled={!resource.id}/>
                ) : (
                    <Tooltip title={t('Select a resource category first')} placement="bottom">
                        <span>
                            <Tab label={t('Working/Opening Hours')} value={'availabilities'} disabled/>
                        </span>
                    </Tooltip>
                )}
                <Tab label={t('Services')} value={'services'} disabled={!resource.id}/>
            </ScrollableTabList>
        </TabContext>
    )

    if (resourceData.loading) {
        return (
            <Box sx={{width: '100%', pb: 8}}>
                {headerSection}
                <DetailsLoading/>
            </Box>
        )
    }

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    gap: 6,
                }}
            >
                {titleSection}
                {tabsSection}
            </Stack>

            {activeTab === 'basic' && (
                <Box sx={{mt: 3}}>
                    <ResourceDetailsForm
                        resourceData={resourceData}
                        updateField={updateField}
                        updateMetaField={updateMetaField}
                        updateChildMetaField={updateChildMetaField}
                        handleRestaurantAutoAssignChange={handleRestaurantAutoAssignChange}
                        handleMakeAllBookable={handleMakeAllBookable}
                        handleShowSeatsAroundTable={handleShowSeatsAroundTable}
                        currentChildResources={currentChildResources}
                    />
                </Box>
            )}

            {activeTab === 'availabilities' && (
                <ResourceAvailabilitiesTab resourceId={id} disabled={disabled} titleSection={titleSection}/>
            )}

            {activeTab === 'services' && (
                <ResourceServicesTab resource={resource} disabled={disabled}/>
            )}
        </Box>
    )
}

export default ResourceDetails
